import {createApp} from 'vue'
import App from './App.vue'
import store from './store'
import router from './router'
import VCalendar from 'v-calendar'
import 'v-calendar/dist/style.css'
import clickOutside from './script/clickOutside'
import {vMaska} from 'maska'
import $bus from './script/eventbus.js'
import VueMathjax from 'vue-mathjax-next'

import layout from './layout/default.vue'
import EmptyLayout from './layout/empty.vue'
import AuthLayout from './layout/authorization.vue'
import InnerLayout from './layout/inner.vue'
import SearchLayout from './layout/searchLayout.vue'
import DocsLayout from './layout/docs.vue'
import CourseList from './layout/courseList.vue'
import Toast from 'vue-toastification'
import * as Sentry from '@sentry/vue'

import BtnWrapper from './components/UI/BtnWrapper.vue'
import Coderunner from '@/components/UI/coderunner/Coderunner.vue'
import {VAceEditor} from 'vue3-ace-editor'
import HTMLView from '@/components/UI/HTMLView.vue'

import './script/cache/catch-errors'

import 'vue-toastification/dist/index.css'
import '@/assets/css/tailwind.css'
import '@/assets/scss/common/main.scss'

import '@/assets/css/fonts.scss'

import {VueHeadMixin, createHead} from '@unhead/vue'

import FloatingVue from 'floating-vue'
import 'floating-vue/dist/style.css'
import {checkSentry} from '@/script'
import {VueQueryPlugin} from 'vue-query'
import {queryClient} from '@/query/queryClient.js'
import {loadExternalTranslations} from "@/i18n";

// Todo: https://learn.javascript.ru/indexeddb https://www.w3.org/TR/IndexedDB/
// В случае необходимости добавить indexeddb для хранения курсов

const i18n = await loadExternalTranslations()

const app = createApp(App)
app.provide('mitt', $bus)

// Packages
app.use(VCalendar, {})
app.use(i18n)
app.use(Toast, {})
app.use(FloatingVue) // ** tooltips and dropdowns (https://floating-vue.starpad.dev/)
app.use(VueMathjax)
app.use(VueQueryPlugin, {
    queryClient
})


// =========================================
// directives
// =========================================
app.directive('click-outside', clickOutside)
app.directive('maska', vMaska)

app.mixin(VueHeadMixin)

// =========================================
//  LAYOUTS
// =========================================
app.component('default', layout)
app.component('empty-layout', EmptyLayout)
app.component('auth-layout', AuthLayout)
app.component('inner-layout', InnerLayout)
app.component('searchLayout', SearchLayout)
app.component('docs-layout', DocsLayout)
app.component('course-list', CourseList)


// =========================================
// UI
// =========================================

app.component('btn-wrapper', BtnWrapper)
app.component('coderunner', Coderunner)
app.component('html-view', HTMLView)
app.component('v-ace-editor', VAceEditor)

const head = createHead()

app.use(store)
app.use(router)
app.use(head)

if (checkSentry()) {
    Sentry.init({
        app,
        dsn: import.meta.env.VITE_APP_SENTRY_DSN,
        integrations: [
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
                maskAllInputs: false,
                networkDetailAllowUrls: [import.meta.env.VITE_APP_BASE_API_URL + 'webservice/rest/server.php']
            }),
            Sentry.replayCanvasIntegration(),
            Sentry.browserTracingIntegration(),
            Sentry.feedbackIntegration({
                // Disable injecting the default widget
                autoInject: false
            }),
            Sentry.breadcrumbsIntegration({
                console: true,
                dom: true,
                fetch: true,
                history: true,
                xhr: true
            }),
            Sentry.extraErrorDataIntegration()
        ],
        // Tracing
        tracesSampleRate: import.meta.env.VITE_APP_SENTRY_SAMPLE_RATE, //  Capture 100% of the transactions
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: [/.*\.php.*/, /.*paidcourses.*/],
        // Session Replay
        replaysSessionSampleRate: import.meta.env.VITE_APP_SENTRY_SAMPLE_RATE, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        profilesSampleRate: 0.0,
        attachStacktrace: 1.0,
        ignoreErrors: [/.*Network\s*Error/,/.*ServiceWorker.*/,/.*Importing.+failed.*/,/.*Failed.*imported.*module.*/],
        release: `lxp@${import.meta.env.PACKAGE_VERSION}`,
        environment: import.meta.env.VITE_APP_SENTRY_ENV,
        tracesSampler: samplingContext => samplingContext.parentSampled ? 1.0 :
            import.meta.env.VITE_APP_SENTRY_SAMPLE_RATE,
        beforeSend: (event) => {
            // Проверяем, есть ли у события title и совпадает ли он с нежелательным значением
            if (event.message && event.message.includes('AbortError: The operation was aborted.')) {
                return null; // отменить отправку
            }

            const errorMessage = event.exception?.values?.[0]?.value;
            if (errorMessage &&
                (
                    errorMessage.includes('Error invoking post: Method not found') ||
                    errorMessage.includes('Failed to execute \'transaction\' on \'IDBDatabase\': The database connection is closing.') ||
                    errorMessage.includes('AbortError: The operation was aborted.') ||
                    errorMessage.includes('\'text/html\' is not a valid JavaScript MIME type.') ||
                    errorMessage.startsWith('error loading dynamically imported module') ||
                    errorMessage.includes('Request aborted')
                )
            ) {
                return null;
            }

            return event; // разрешить отправку
        },
    })
}

app.mount('#app')
